import { config as defaultConfig } from '../config/default.config';
import { deepMerge, getAlias } from '../utils/utils';
import { ExistingCard } from '../types/api';

/** Placeholder token used in file paths that need the tenant-root URL. */
const ROOT_PLACEHOLDER = '[ROOT]';

let finalConfig = defaultConfig;
let configError = false;

export const initializeConfig = async (): Promise<void> => {
  try {
    const response = await fetch('/config/runtime-config.json');
    const runtimeConfig = response.ok ? await response.json() : {};
    finalConfig = deepMerge(defaultConfig, runtimeConfig);
    
    if (finalConfig.ConfigServer.Enabled) {      
      const aliasName = getAlias();
      const response = await fetch(`${finalConfig.ConfigServer.BaseAddress}/api/v1/s2p/runtime-configs/${aliasName}`);
      if (!response.ok) {
        configError = true;
        console.error(`ConfigServer returned ${response.status} for alias "${aliasName}"`);
      } else {
        const SiteConfig = await response.json();
        finalConfig = deepMerge(finalConfig, SiteConfig);
      }
    }
    // process dynamic config template variables.
    finalConfig.API.LOGIN_URL = finalConfig.API.LOGIN_URL.replace('{alias}', getAlias());
  } catch (error) {
    configError = true;
    console.error('Error initializing config:', error);
  }
};

export const getConfig = () => finalConfig;
export const getConfigError = () => configError;

/**
 * Extracts the tenant-root URL from ASSETS.BASE_URL.
 *
 * CDN pattern: .../public/tenants/{tenantId}/...  -> up to tenantId
 * Localhost:   http://localhost:3000/assets        -> origin only
 */
export const getCdnRoot = (): string => {
  const baseUrl = finalConfig.ASSETS.BASE_URL;
  const tenantMatch = baseUrl.match(/^(.*\/public\/tenants\/[^/]+)/);
  if (tenantMatch) return tenantMatch[1];

  // Fallback: return origin (scheme + host + port)
  try {
    const url = new URL(baseUrl);
    return url.origin;
  } catch {
    return baseUrl;
  }
};

/**
 * Resolves a file path that may contain the ROOT_PLACEHOLDER.
 * - Paths containing ROOT_PLACEHOLDER -> replace with getCdnRoot()
 * - Other paths -> prepend ASSETS.BASE_URL (existing behaviour)
 */
export const resolveFileUrl = (path: string | null): string | null => {
  if (!path) return null;
  if (path.includes(ROOT_PLACEHOLDER)) {
    return path.replace(ROOT_PLACEHOLDER, getCdnRoot());
  }
  return `${finalConfig.ASSETS.BASE_URL}${path}`;
};

/**
 * Returns real user cards when available.
 * Falls back to debug dummy cards when debug.enabled is true and no real cards exist.
 */
export const resolveExistingCards = (userCards?: ExistingCard[]): ExistingCard[] | undefined => {
  if (userCards && userCards.length > 0) return userCards;

  const cfg = finalConfig as any;
  if (cfg.debug?.enabled && cfg.debug?.dummyCards?.length > 0) {
    return cfg.debug.dummyCards as ExistingCard[];
  }

  return undefined;
}; 